import { Sparkles, Droplets, Zap } from "lucide-react";
import { useLanguage } from "@/lib/i18n";

interface Ingredient {
  name: string;
  description: string;
  icon?: 'sparkles' | 'droplets' | 'zap';
  iconColor?: string;
  image?: string;
}

interface IngredientsSectionProps {
  ingredients: Ingredient[];
  title?: string;
}

const iconMap = {
  sparkles: Sparkles,
  droplets: Droplets,
  zap: Zap,
};

const colorMap: Record<string, string> = {
  pink: 'text-primary',
  blue: 'text-blue-500',
  amber: 'text-amber-500',
  emerald: 'text-emerald-500',
};

export function IngredientsSection({ ingredients, title }: IngredientsSectionProps) {
  const { t } = useLanguage();
  
  return (
    <section className="mb-8 md:mb-10">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-5 md:mb-7">
        {title || t('product.ingredients')}
      </h2>
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 ${ingredients.length === 5 ? 'md:grid-cols-5' : 'md:grid-cols-3'}`}>
        {ingredients.map((ingredient, idx) => {
          const IconComponent = ingredient.icon ? iconMap[ingredient.icon] : Sparkles;
          const iconColorClass = ingredient.iconColor ? colorMap[ingredient.iconColor] || 'text-primary' : 'text-primary';
          
          return (
            <div key={idx} className="p-4 md:p-6 bg-white/70 rounded-2xl border border-white/80 shadow-sm">
              {ingredient.image ? (
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 rounded-2xl bg-gradient-to-br from-secondary to-pink-200 flex items-center justify-center p-2">
                  <img src={ingredient.image} alt={ingredient.name} className="w-10 h-10 object-contain" />
                </div>
              ) : (
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 rounded-2xl bg-gradient-to-br from-secondary to-pink-200 flex items-center justify-center">
                  <IconComponent className={`w-7 h-7 md:w-8 md:h-8 ${iconColorClass}`} />
                </div>
              )}
              <h3 className="font-semibold text-center text-sm md:text-base mb-1 md:mb-2">{ingredient.name}</h3>
              <p className="text-xs md:text-sm text-muted-foreground text-center leading-relaxed">
                {ingredient.description}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
